// Profile and ManageConsents still contain local name-formatting for full
// names and demographics fallbacks. Navbar initials and greeting first-name
// helpers live here so digit-free display rules stay consistent.

export type AuthUserLike = {
  given_name?: string | null
  name?: string | null
}

export type ProfileNameParts = {
  firstName: string | null
  lastName: string | null
  /** IMDB demographic email. Used for navbar initials when the name is missing. */
  emailAddress: string | null
}

export function looksLikeEmail(value: string | null | undefined): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value?.trim() ?? '')
}

export function firstNameFromDisplayName(value: string | null | undefined): string | null {
  if (!value || looksLikeEmail(value)) return null
  return value.trim().split(/\s+/)[0] || null
}

export function firstNameFromAuthUser(user: AuthUserLike | null | undefined): string | null {
  return firstNameFromDisplayName(user?.given_name) ?? firstNameFromDisplayName(user?.name)
}

function containsDigit(value: string): boolean {
  return /\d/.test(value)
}

function normalizeNamePart(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? ''
  if (!trimmed || containsDigit(trimmed)) {
    return null
  }
  return trimmed
}

function digitFreeInitials(value: string | null): string | null {
  if (!value || containsDigit(value)) {
    return null
  }
  return value
}

function initialsFromParts(parts: string[]): string | null {
  const usable = parts.filter(Boolean)
  if (usable.length === 0) {
    return null
  }

  if (usable.length === 1) {
    return digitFreeInitials(usable[0].slice(0, 2).toLocaleUpperCase('en-US'))
  }

  const first = usable[0].charAt(0)
  const last = usable[usable.length - 1].charAt(0)
  return digitFreeInitials(`${first}${last}`.toLocaleUpperCase('en-US'))
}

function initialsFromEmail(email: string): string | null {
  const atIndex = email.indexOf('@')
  const localPart = atIndex === -1 ? email : email.slice(0, atIndex)
  return initialsFromParts(localPart.split(/[._-]/))
}

/**
 * Navbar initials from IMDB demographics only.
 * Same order as Butler: a real name, then the email local-part, then 'U'.
 * Parts that contain a digit are skipped so a phone stored as a name cannot
 * become the avatar. Auth0 claims are not an input.
 */
export function deriveAvatarInitials(profile: ProfileNameParts | null | undefined): string {
  const first = profile?.firstName?.trim() ?? ''
  const last = profile?.lastName?.trim() ?? ''
  const rawName = [first, last].filter(Boolean).join(' ')

  if (rawName && !looksLikeEmail(rawName)) {
    const fromName = initialsFromParts(
      [normalizeNamePart(first), normalizeNamePart(last)].filter((part): part is string => part != null)
    )
    if (fromName) {
      return fromName
    }
  }

  const rawEmail = profile?.emailAddress?.trim() ?? ''
  const emailSource = rawEmail || (looksLikeEmail(rawName) ? rawName : '')
  if (emailSource) {
    const fromEmail = initialsFromEmail(emailSource)
    if (fromEmail) {
      return fromEmail
    }
  }

  return 'U'
}
